import styled, { css } from "styled-components";
import {
  brand,
  border,
  status,
  surface,
  text,
  typography,
  radius,
} from "../../theme/tokens";

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export const QueuePageWrapper = styled.div`
  font-family: ${typography.fontFamily};
  flex: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: ${surface.surface};
`;

export const QueueHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid ${border.default};
`;

export const QueueTitle = styled.h1`
  margin: 0;
  font-size: 18px;
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
`;

export const ViewContainer = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  position: relative;
`;

// Both queue views stay mounted and stacked; only one is interactable.
// This is TabView's technique (the matter viewer): hide with
// visibility/opacity — never unmount, never display:none — so the
// Apryse document engine survives letter-to-letter transitions instead
// of paying a full WASM boot per letter, and its canvases keep their
// layout while hidden.
export const ViewLayer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  visibility: ${({ $active }) => ($active ? "visible" : "hidden")};
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  pointer-events: ${({ $active }) => ($active ? "auto" : "none")};
  z-index: ${({ $active }) => ($active ? 1 : 0)};
`;

// ---------------------------------------------------------------------------
// List view
// ---------------------------------------------------------------------------

export const ListViewWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 24px;
  overflow: hidden;
`;

export const ListToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const ListTableWrapper = styled.div`
  flex: 1;
  overflow: auto;
`;

const QUEUE_STATUS_TONES = {
  extracting: status.processing,
  ready: status.review,
  confirmed: status.ready,
  dismissed: status.neutral,
};

export const StatusPill = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: ${radius.xl};
  font-size: 12px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;

  ${({ status: s }) => {
    const tone = QUEUE_STATUS_TONES[s] || status.neutral;
    return css`
      background: ${tone.bg};
      color: ${tone.fg};
      border: 1px solid ${tone.border};
    `;
  }}
`;

/** "The two detection signals disagreed" marker — red because these
 * rows need a human ruling (dismiss or proceed), not just data entry. */
export const DetectionFlagPill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${radius.xl};
  font-size: 11px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  background: ${status.failed.bg};
  color: ${status.failed.fg};
  border: 1px solid ${status.failed.border};
`;

/** Payload paid-state pill — tone names come from
 * `describePaymentStatus` in utils.js, not raw Payload statuses, so
 * new statuses degrade to neutral instead of an unstyled pill. */
const PAYMENT_TONES = {
  paid: status.ready,
  partial: status.review,
  problem: status.failed,
  pending: status.processing,
};

export const PaymentPill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${radius.xl};
  font-size: 11px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;

  ${({ tone }) => {
    const t = PAYMENT_TONES[tone] || status.neutral;
    return css`
      background: ${t.bg};
      color: ${t.fg};
      border: 1px solid ${t.border};
    `;
  }}
`;

const SF_WRITE_TONES = {
  succeeded: status.ready,
  failed: status.failed,
  disabled: status.neutral,
  not_attempted: status.neutral,
};

export const SfWriteStatusPill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${radius.xl};
  font-size: 11px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;

  ${({ status: s }) => {
    const tone = SF_WRITE_TONES[s] || status.neutral;
    return css`
      background: ${tone.bg};
      color: ${tone.fg};
      border: 1px solid ${tone.border};
    `;
  }}
`;

export const MutedText = styled.span`
  color: ${text.disabled};
`;

export const MetaText = styled.span`
  color: ${text.tertiary};
  font-size: 13px;
`;

export const ResultName = styled.div`
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
`;

export const ResultMeta = styled.div`
  font-size: 12px;
  color: ${text.tertiary};
  margin-top: 2px;
`;

// ---------------------------------------------------------------------------
// Review view
// ---------------------------------------------------------------------------

export const ReviewLayout = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

// Scrolling belongs to the BODY, not the whole column. While the
// sidebar scrolled as one piece, the tab row, the cost-invoice strip
// and the Confirm button all scrolled away with the fields — so a
// status strip could not stay in front of the reviewer, which is the
// entire point of having one (Matthew, 2026-08-28).
export const ReviewSidebar = styled.div`
  width: 380px;
  min-width: 380px;
  border-right: 1px solid ${border.default};
  display: flex;
  flex-direction: column;
  background: ${surface.surface};
  overflow: hidden;
`;

export const ReviewSidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid ${border.light};
`;

export const ReviewSidebarTitle = styled.div`
  font-size: 11px;
  font-weight: ${typography.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${text.disabled};
`;

export const ReviewSidebarBody = styled.div`
  flex: 1;
  /* min-height: 0 is what actually lets a flex child scroll instead of
     growing the column past its parent. */
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

// Pinned between the tab row and the scrolling fields: what this letter
// owes the client, visible on both tabs and at every scroll position.
export const InvoiceStripSlot = styled.div`
  flex: 0 0 auto;
  padding: 10px 20px 0;
`;

export const ReviewSidebarFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${border.light};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SectionHeading = styled.div`
  font-size: 11px;
  font-weight: ${typography.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${text.tertiary};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const DocumentViewerPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #525659;
  overflow: hidden;
`;

export const ViewerToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: #3b3f42;
  color: ${text.onAccent};
  font-size: 13px;
  min-height: 40px;
`;

export const ViewerBody = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
  align-items: stretch;
  justify-content: center;
  overflow: hidden;
`;

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

export const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: ${typography.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${text.tertiary};
`;

export const TextInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 14px;
  color: ${text.primary};
  outline: none;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.2s;

  &::placeholder {
    color: ${text.disabled};
  }
  &:focus {
    border-color: ${brand.primary};
    box-shadow: 0 0 0 2px rgba(12, 83, 187, 0.08);
  }
  &:disabled {
    background: ${surface.hover};
    color: ${text.disabled};
    cursor: not-allowed;
  }
`;

export const InputSuffix = styled.div`
  position: relative;

  input {
    padding-right: 28px;
  }

  span {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 13px;
    color: ${text.disabled};
    pointer-events: none;
  }
`;

export const InputPrefix = styled.div`
  position: relative;

  input {
    padding-left: 22px;
  }

  span {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 13px;
    color: ${text.disabled};
    pointer-events: none;
  }
`;

export const EvidenceSnippet = styled.blockquote`
  margin: 6px 0 0;
  padding: 8px 10px;
  background: ${surface.folderTint};
  border-left: 3px solid ${border.default};
  border-radius: 0 ${radius.sm} ${radius.sm} 0;
  font-size: 12px;
  font-style: italic;
  color: ${text.tertiary};
  line-height: 1.45;
`;

// ---------------------------------------------------------------------------
// Matter search / selection
// ---------------------------------------------------------------------------

export const SearchWrapper = styled.div`
  position: relative;
`;

export const SearchResults = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background: ${surface.surface};
  border: 1px solid ${border.default};
  border-radius: ${radius.lg};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 100;
`;

export const SearchResultItem = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid ${surface.page};
  transition: background 0.12s;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${brand.primaryLight};
  }
`;

export const SearchResultEmpty = styled.div`
  padding: 14px;
  text-align: center;
  color: ${text.disabled};
  font-size: 13px;
`;

export const SelectedCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f0f7ff;
  border: 1px solid #c3d5f1;
  border-radius: ${radius.md};
`;

export const ClearBtn = styled.button`
  background: none;
  border: none;
  color: ${text.tertiary};
  cursor: pointer;
  padding: 2px 4px;
  display: inline-flex;
  align-items: center;
  line-height: 1;
  border-radius: ${radius.sm};
  &:hover {
    color: ${status.failed.fg};
    background: ${status.failed.bg};
  }
  &:focus-visible {
    outline: 2px solid ${border.focus};
    outline-offset: 1px;
  }
`;

// Client-record card at the top of the review sidebar — same posture as
// the mail sorter's ExplainPanel identity card, so matching a letter to
// a client feels identical across both queues.
export const IdentityPanel = styled.div`
  padding: 10px 12px;
  background: ${surface.folderTint};
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
`;

export const IdentityRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: ${text.secondary};
`;

// Read-only: reading who the suggestion is for must never commit it.
// Accepting happens through UseMatchButton below.
export const AutoMatchBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  background: ${status.review.bg};
  border: 1px solid ${status.review.border};
  border-radius: ${radius.md};
`;

export const UseMatchButton = styled.button`
  align-self: stretch;
  padding: 6px 12px;
  background: ${brand.primary};
  color: #fff;
  border: none;
  border-radius: ${radius.sm};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: ${brand.primaryHover};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const AutoMatchLabel = styled.div`
  font-size: 10px;
  font-weight: ${typography.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${status.review.fg};
  margin-bottom: 4px;
`;

// ---------------------------------------------------------------------------
// Duplicate flags / warning banners
// ---------------------------------------------------------------------------

export const WarningBanner = styled.div`
  padding: 10px 12px;
  background: ${({ tone }) =>
    tone === "strong" ? status.failed.bg : status.review.bg};
  border: 1px solid
    ${({ tone }) => (tone === "strong" ? status.failed.border : status.review.border)};
  border-radius: ${radius.md};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const WarningHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const WarningLabel = styled.div`
  font-size: 11px;
  font-weight: ${typography.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${({ tone }) => (tone === "strong" ? status.failed.fg : status.review.fg)};
`;

export const WarningBody = styled.div`
  font-size: 12px;
  color: ${text.secondary};
  line-height: 1.4;
`;

export const DegradedBanner = styled.div`
  padding: 8px 10px;
  background: ${status.review.bg};
  border: 1px solid ${status.review.border};
  border-radius: ${radius.md};
  font-size: 12px;
  color: ${status.review.fg};
`;

// ---------------------------------------------------------------------------
// Resolutions list ("dates first, amounts behind a dropdown")
// ---------------------------------------------------------------------------

export const ResolutionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ResolutionRow = styled.div`
  border: 1px solid ${border.light};
  border-radius: ${radius.md};
  overflow: hidden;
`;

export const ResolutionSummary = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: ${surface.folderTint};
  border: none;
  cursor: pointer;
  font-family: ${typography.fontFamily};
  font-size: 13px;
  color: ${text.primary};
  text-align: left;

  &:hover {
    background: ${surface.hover};
  }
`;

export const ResolutionDetails = styled.div`
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: ${text.secondary};
  border-top: 1px solid ${border.light};
`;

export const ResolutionDetailRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

// ---------------------------------------------------------------------------
// Chips (referring attorney / G&P yes-no)
// ---------------------------------------------------------------------------

export const ChipRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: ${radius.xl};
  font-size: 12px;
  font-weight: ${typography.weight.semibold};
  background: ${({ $on }) => ($on ? status.ready.bg : surface.hover)};
  color: ${({ $on }) => ($on ? status.ready.fg : text.tertiary)};
  border: 1px solid ${({ $on }) => ($on ? status.ready.border : border.default)};
`;

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

export const PrimaryBtn = styled.button`
  width: 100%;
  padding: 11px 20px;
  background: ${brand.primary};
  color: ${text.onAccent};
  border: none;
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: background 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: ${brand.primaryHover};
  }
  &:disabled {
    background: #c3d5f1;
    cursor: not-allowed;
  }
`;

// Sending the invoice is the only action on the fee notice panel that
// reaches the client, so it does not share the blue used by every other
// primary button — Matthew, 2026-08-28, after four invoices were created
// and none sent. Green and alone above the row, so "created" can never
// again be mistaken for "sent".
export const SendBtn = styled(PrimaryBtn)`
  background: ${status.ready.fg};

  &:hover:not(:disabled) {
    background: #0b5e2f;
  }
  &:disabled {
    background: ${status.ready.border};
    cursor: not-allowed;
  }
`;

export const SecondaryBtn = styled.button`
  flex: 1;
  padding: 9px 16px;
  background: ${surface.surface};
  color: ${text.tertiary};
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 13px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: ${surface.hover};
    color: ${text.primary};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DangerBtn = styled.button`
  flex: 1;
  padding: 9px 16px;
  background: ${surface.surface};
  color: ${status.failed.fg};
  border: 1px solid ${status.failed.border};
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 13px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: ${status.failed.bg};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const LinkBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${brand.primary};
  font-family: ${typography.fontFamily};
  font-size: 12px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  text-decoration: underline;

  &:hover:not(:disabled) {
    color: ${brand.primaryHover};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const BtnRow = styled.div`
  display: flex;
  gap: 8px;
`;

// ---------------------------------------------------------------------------
// Empty / loading states
// ---------------------------------------------------------------------------

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
  flex: 1;
`;

export const EmptyTitle = styled.div`
  font-size: 16px;
  font-weight: ${typography.weight.semibold};
  color: ${text.tertiary};
  margin-bottom: 8px;
`;

export const EmptyText = styled.div`
  font-size: 13px;
  color: ${text.disabled};
  max-width: 320px;
`;

export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: ${text.disabled};
  font-size: 13px;
  flex: 1;
`;

// ---------------------------------------------------------------------------
// Invoice panel (view/edit/send the cost-reimbursement invoice — Matthew,
// 2026-07-27 — without leaving the letter's review screen)
// ---------------------------------------------------------------------------

export const SidebarTabRow = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px 16px 0;
  border-bottom: 1px solid ${border.light};
  background: ${surface.surface};
  flex-shrink: 0;
`;

// Marks the Cost Invoice tab when costs are owed and nothing has been
// sent — the one state where missing it costs the firm the money it
// advanced.
export const TabDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-left: 6px;
  border-radius: 50%;
  background: ${status.review.fg};
  vertical-align: middle;
`;

export const SidebarTab = styled.button`
  padding: 8px 14px;
  border: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? brand.primary : "transparent")};
  background: transparent;
  font-family: ${typography.fontFamily};
  font-size: 13px;
  font-weight: ${typography.weight.semibold};
  color: ${({ $active }) => ($active ? brand.primary : text.tertiary)};
  cursor: pointer;
  transition: color 0.15s;

  &:hover:not(:disabled) {
    color: ${brand.primary};
  }
  &:disabled {
    color: ${text.disabled};
    cursor: not-allowed;
  }
`;

export const CostLinesTable = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  overflow: hidden;
`;

export const CostLineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid ${border.light};
  background: ${surface.surface};

  &:last-child {
    border-bottom: none;
  }
`;

export const CostLineMeta = styled.div`
  font-size: 11px;
  color: ${text.disabled};
  margin-top: 2px;
`;

export const CostTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 10px 10px;
  background: ${surface.hover};
  font-weight: ${typography.weight.semibold};
  font-size: 13px;
  color: ${text.primary};
`;

export const EmailPreview = styled.pre`
  margin: 0;
  padding: 12px;
  background: ${surface.hover};
  border: 1px solid ${border.light};
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 12px;
  line-height: 1.55;
  color: ${text.secondary};
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 320px;
  overflow-y: auto;
`;

export const SentBanner = styled.div`
  padding: 10px 12px;
  border-radius: ${radius.md};
  background: ${status.ready.bg};
  border: 1px solid ${status.ready.border};
  color: ${status.ready.fg};
  font-size: 12px;
  line-height: 1.5;
`;

// Team Activity: the count of invoices a reviewer created and never
// sent. Loud on purpose — it is the number that says work has stalled
// and a client is waiting.
export const UnsentPill = styled.span`
  display: inline-block;
  margin-top: 4px;
  padding: 1px 7px;
  border-radius: 999px;
  background: ${status.failed.bg};
  border: 1px solid ${status.failed.border};
  color: ${status.failed.fg};
  font-size: 11px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: 0.02em;
`;

// The counterpart to SentBanner, and the reason it exists: an invoice
// that has been created but never sent was the one state the panel said
// nothing about, so four of them went unnoticed on 2026-08-28. Same
// weight as the green, opposite colour, and semibold because it is a
// warning rather than a status line.
export const UnsentBanner = styled.div`
  padding: 10px 12px;
  border-radius: ${radius.md};
  background: ${status.failed.bg};
  border: 1px solid ${status.failed.border};
  color: ${status.failed.fg};
  font-size: 12px;
  line-height: 1.5;
  font-weight: ${typography.weight.semibold};
`;
